import React from "react";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { FontStyleOverride, ProjectFontDefinition } from "src/components/types";
import "./SingleFontStyleEditor.css";
import { ContentCopy, Delete } from "@mui/icons-material";

type SingleFontStyleEditorProps = {
  override: FontStyleOverride;
  projectFonts: Array<ProjectFontDefinition>;
  onDelete: (id: string) => void;
  layerNames: Array<string>;
  onChange: (newValue: FontStyleOverride) => void;
  onCopy: (id: string) => void;
  cardParameters: Array<string>;
};

function SingleFontStyleEditor({
  override,
  projectFonts,
  onDelete,
  layerNames,
  onChange,
  onCopy,
  cardParameters,
}: SingleFontStyleEditorProps) {
  function textInputOnChange(id: string, e: React.ChangeEvent<HTMLInputElement>) {
    let value = e.target.value;
    let fieldName = e.target.name;
    let overrideCopy = { ...override, [fieldName]: value };
    onChange(overrideCopy);
  }

  function layerSelectOnChange(e: SelectChangeEvent<unknown>) {
    let newLayerName = e.target.value as string;
    let overrideCopy = { ...override, layerName: newLayerName };
    onChange(overrideCopy);
  }

  function fontSelectOnChange(e: SelectChangeEvent<unknown>) {
    let selectedFontId = e.target.value as string;
    let selectedFont = projectFonts.find((font) => font.id === selectedFontId);
    if (selectedFont) {
      let overrideCopy = {
        ...override,
        projectFont: selectedFont.fontName,
        fontFamily: selectedFont.fontFamily,
        fontWeight: selectedFont.fontWeight,
        fontStyle: selectedFont.fontStyle,
        fontSize: selectedFont.fontSize,
        fontColor: selectedFont.fontColor,
        strokeWidth: selectedFont.fontStrokeWidth,
        strokeColor: selectedFont.fontStrokeColor,
      };
      onChange(overrideCopy);
    }
  }

  function sizeInputOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>, attributeName: string) {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      let value = e.currentTarget.value;
      const sizeRegex = /^(-?\d+(?:\.\d+)?)([a-z%]*)$/i;
      const match = value.trim().match(sizeRegex);
      if (!match) return;
      let num = parseFloat(match[1]);
      const unit = match[2] || "px";
      if (e.key === "ArrowUp") {
        num = Math.round((num + 1) * 100) / 100;
      } else if (e.key === "ArrowDown") {
        num = Math.round((num - 1) * 100) / 100;
      }
      const overrideCopy = { ...override, [attributeName]: `${num}${unit}` };
      onChange(overrideCopy);
    }
  }

  function conditionParameterOnChange(e: SelectChangeEvent<unknown>) {
    let newConditionParameter = e.target.value as string;
    let overrideCopy = { ...override, conditionParameter: newConditionParameter };
    onChange(overrideCopy);
  }

  function hasActiveCondition() {
    return override.conditionParameter && override.conditionValue;
  }

  return (
    <div className="single-font-editor">
      <span style={{ alignSelf: "center" }}>Layer:</span>
      <Select
        size="small"
        onChange={layerSelectOnChange}
        value={layerNames.find((layer) => layer === override.layerName)}
      >
        {layerNames
          .sort((a, b) => a.localeCompare(b))
          .map((layer) => (
            <MenuItem key={layer} value={layer}>
              {layer}
            </MenuItem>
          ))}
      </Select>
      <label htmlFor={`subtext-id-${override.id}`}>Subtext ID:</label>
      <input
        id={`subtext-id-${override.id}`}
        type="text"
        name="subtextId"
        value={override.subtextId}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <span style={{ alignSelf: "center" }}>Project font:</span>
      <Select
        size="small"
        onChange={fontSelectOnChange}
        value={projectFonts.find((font) => font.fontName === override.projectFont)?.id || ""}
      >
        {projectFonts.map((font) => (
          <MenuItem key={font.id} value={font.id}>
            {font.fontName}
          </MenuItem>
        ))}
      </Select>
      <label htmlFor={`font-family-${override.id}`}>Family:</label>
      <input
        id={`font-family-${override.id}`}
        type="text"
        name="fontFamily"
        value={override.fontFamily}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`font-weight-${override.id}`}>Weight:</label>
      <input
        id={`font-weight-${override.id}`}
        type="text"
        name="fontWeight"
        value={override.fontWeight}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`font-style-${override.id}`}>Style:</label>
      <input
        id={`font-style-${override.id}`}
        type="text"
        name="fontStyle"
        value={override.fontStyle}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`font-size-${override.id}`}>Size:</label>
      <input
        id={`font-size-${override.id}`}
        type="text"
        name="fontSize"
        value={override.fontSize}
        onChange={(e) => textInputOnChange(override.id, e)}
        onKeyDown={(e) => sizeInputOnKeyDown(e, "fontSize")}
      />
      <label htmlFor={`font-color-${override.id}`}>Color:</label>
      <input
        id={`font-color-${override.id}`}
        type="text"
        name="fontColor"
        value={override.fontColor}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`line-height-${override.id}`}>Line Ht:</label>
      <input
        id={`line-height-${override.id}`}
        type="text"
        name="lineHeight"
        value={override.lineHeight}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`icon-height-${override.id}`}>Icon Ht:</label>
      <input
        id={`icon-height-${override.id}`}
        type="text"
        name="iconHeight"
        value={override.iconHeight}
        onChange={(e) => textInputOnChange(override.id, e)}
        onKeyDown={(e) => sizeInputOnKeyDown(e, "iconHeight")}
      />
      <label htmlFor={`icon-margin-${override.id}`}>Icon Mrgn:</label>
      <input
        id={`icon-margin-${override.id}`}
        type="text"
        name="iconMargin"
        value={override.iconMargin}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`stroke-width-${override.id}`}>Stroke W:</label>
      <input
        id={`stroke-width-${override.id}`}
        type="text"
        name="strokeWidth"
        value={override.strokeWidth}
        onChange={(e) => textInputOnChange(override.id, e)}
        onKeyDown={(e) => sizeInputOnKeyDown(e, "strokeWidth")}
      />
      <label htmlFor={`stroke-color-${override.id}`}>Stroke C:</label>
      <input
        id={`stroke-color-${override.id}`}
        type="text"
        name="strokeColor"
        value={override.strokeColor}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`margin-${override.id}`}>Margin:</label>
      <input
        id={`margin-${override.id}`}
        type="text"
        name="margin"
        value={override.margin}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <label htmlFor={`text-align-${override.id}`}>Text Align:</label>
      <input
        id={`text-align-${override.id}`}
        type="text"
        name="textAlign"
        value={override.textAlign}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      <span style={{ flexGrow: 1, fontWeight: "bold", gridColumn: "span 2" }}>Condition</span>
      <span style={{ alignSelf: "center" }}>Parameter:</span>
      <Select
        size="small"
        onChange={conditionParameterOnChange}
        value={override.conditionParameter}
        id={`condition-parameter-${override.id}`}
      >
        {cardParameters
          .sort((a, b) => a.localeCompare(b))
          .map((param) => (
            <MenuItem key={param} value={param}>
              {param}
            </MenuItem>
          ))}
      </Select>
      <label htmlFor={`condition-value-${override.id}`}>Value:</label>
      <input
        id={`condition-value-${override.id}`}
        type="text"
        name="conditionValue"
        value={override.conditionValue}
        onChange={(e) => textInputOnChange(override.id, e)}
      />
      {hasActiveCondition() && (
        <>
          <label htmlFor={`condition-order-${override.id}`}>Order:</label>
          <input
            id={`condition-order-${override.id}`}
            type="number"
            name="conditionOrder"
            value={override.conditionOrder}
            onChange={(e) => textInputOnChange(override.id, e)}
          />
        </>
      )}
      <button className="single-font-delete" onClick={() => onDelete(override.id)}>
        <Delete />
      </button>
      <button className="single-font-copy" onClick={() => onCopy(override.id)}>
        <ContentCopy />
      </button>
    </div>
  );
}

export default SingleFontStyleEditor;
